import { Location } from '@angular/common'
import { AfterViewInit, Component, ErrorHandler, HostListener, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, signal } from '@angular/core'
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms'
import { FieldTree, form, required, validate } from '@angular/forms/signals'
import { Router } from '@angular/router'

import { MatDialog } from '@angular/material/dialog'
import { MatInput } from '@angular/material/input'
import { MatSnackBar } from '@angular/material/snack-bar'

import { KayttajaService } from '../_angular/service/kayttaja.service'
import { LoginInputData, LoginService } from '../_angular/service/login.service'
import { VersionTarkistusPalvelu } from '../_angular/service/version-tarkistus.palvelu'

import { arvonMuutokset } from '../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from '../_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from '../_jaettu-angular/service/form-validation.service'
import { LadataanService } from '../_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from '../_jaettu-angular/service/lemon-translation.service'

import { VaihdaKieliDialog, VaihdaKieliDialogData } from '../vaihda-kieli.dialog'

import { ResendSmsRequest, TwoFactorLoginRequest, TwoFactorLoginResponse } from '@lemon/core-firebase/model/login'
import { ErrorResponse } from '@lemon/core/model/common'

import { CurrencyService } from '@lemon/core/service/currency.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonaid.service'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

interface TunnistuskoodiLomake {
  code: string
}

@Component({
  templateUrl: './second-factor.component.html',
  styleUrls: ['./login.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SecondFactorComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(MatInput, { static: true }) codeInput: MatInput

  private readonly _palvelimenVirhe = signal<string | null>(null)

  lomake = form(signal<TunnistuskoodiLomake>({ code: '' }), polku => {
    required(polku.code)
    validoiReaktiivisesti(polku.code, kontrolli => this._containsNonNumbersValidator(kontrolli))
    validate(polku.code, () => {
      const laji = this._palvelimenVirhe()
      return laji ? { kind: laji } : null
    })
  })
  readonly commonError = signal<string | null>(null)
  online = true
  year = new Date().getFullYear()
  inputReceived: LoginInputData

  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  private _loginInflight: boolean = false

  constructor(
    private _kayttajaService: KayttajaService,
    private _dialog: MatDialog,
    private _router: Router,
    private _validationService: FormValidationService,
    private _ladataanService: LadataanService,
    private _translationService: LemonTranslationService,
    private _versionTarkistaja: VersionTarkistusPalvelu,
    private _loginService: LoginService,
    private _errorHandler: ErrorHandler,
    private _snackbar: MatSnackBar,
    private _versionTarkistusPalvelu: VersionTarkistusPalvelu,
    private _location: Location,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _currencyService: CurrencyService,
  ) {
    // Preheat
    this._firebaseLemonaid.functionsCall<string, void>('loginTarkistaTwoFactorKoodiaJaTallennaTyojonoon', '')
    // Palvelimen virhe on voimassa, kunnes kentän arvo muuttuu, kuten reaktiivisen lomakkeen setErrors
    arvonMuutokset(this.lomake.code).subscribe(() => this._palvelimenVirhe.set(null))
  }

  ngOnInit(): void {
    this._loginService.currentLoginCredentialsObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(data => {
      this.inputReceived = data
      if (!this.inputReceived || !this.inputReceived.email || !this.inputReceived.password || !this.inputReceived.loginCodeId) {
        this.commonError.set(this._translationService.lokalisoi('login.login-out-of-date'))
      }
    })

    this._versionTarkistaja.sovelluksenVersioObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(versiotiedot => {
      this._versionTarkistaja.tarkistaVersio(versiotiedot)
    })

    this._kayttajaService.kayttajanTiedotObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(kayttajanTiedot => {
      if (kayttajanTiedot) {
        this.setLoginProcessEnded()
        this._router.navigate(['/'])
      }
    })
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.codeInput.focus()
    }, 0)
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  setLoginProcessStarted() {
    this._ladataanService.aloitaLataaminen()
  }

  setLoginProcessEnded() {
    this._ladataanService.lopetaLataaminen()
    this._loginInflight = false
  }

  back() {
    this._location.back()
  }
  sendCodeAgain() {
    if (this._loginInflight) {
      return
    }
    this._loginInflight = true

    this.setLoginProcessStarted()

    const resendRequest: ResendSmsRequest = {
      email: this.inputReceived.email,
      password: this.inputReceived.password,
      loginCodeId: this.inputReceived.loginCodeId,
      valittuKieli: this._translationService.nykyinenKieli,
      env: 'w',
      ver: this._versionTarkistusPalvelu.annaVersio(),
      device: navigator.platform,
      os: navigator.userAgent,
    }
    const resend = this._firebaseLemonaid
      .functionsCall<ResendSmsRequest, ErrorResponse>('loginLahettaaUusiKoodi', resendRequest)
      .then(resp => {
        if (!resp) {
          this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe'))
          this._errorHandler.handleError('no-response-or-no-data')
          this.setLoginProcessEnded()
          return
        }

        if (resp.e) {
          this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe'))
          this._errorHandler.handleError(resp.e)
        } else {
          this._snackbar.open(this._translationService.lokalisoi('login.send-success'), 'OK', { duration: 5000, verticalPosition: 'top' })
          setTimeout(() => {
            this.codeInput.focus()
          }, 0)
        }
        this.setLoginProcessEnded()
      })
      .catch(err => {
        this._errorHandler.handleError(err)
        this.setLoginProcessEnded()
        this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe'))
        this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      })
  }
  @HostListener('document:keydown.enter')
  tryCode() {
    if (this._loginInflight) {
      return
    }
    this._loginInflight = true
    if (!this.lomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      setTimeout(() => {
        this.codeInput.focus()
      }, 0)
      this._loginInflight = false
      return
    }
    this.setLoginProcessStarted()
    const request: TwoFactorLoginRequest = {
      email: this.inputReceived.email,
      password: this.inputReceived.password,
      loginCodeId: this.inputReceived.loginCodeId,
      smsCode: '' + (this.lomake.code().value() || '').trim(),
      valittuKieli: this._translationService.nykyinenKieli,
      env: 'w',
      ver: this._versionTarkistusPalvelu.annaVersio(),
      device: navigator.platform,
      os: navigator.userAgent,
    }
    const callForCodeCheck = this._firebaseLemonaid
      .functionsCall<TwoFactorLoginRequest, TwoFactorLoginResponse>('loginTarkistaTwoFactorKoodiaJaTallennaTyojonoon', request)
      .then(data => {
        if (!data) {
          this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe'))
          this._errorHandler.handleError('no-response-or-no-data')
          this.setLoginProcessEnded()
          return
        }

        const error = data.e
        if (error) {
          this.setLoginProcessEnded()
          switch (error) {
            case 'auth/invalid-code':
              this._palvelimenVirhe.set('wrongCode')
              break
            case 'auth/no-valid-login':
              this.commonError.set(this._translationService.lokalisoi('login.login-out-of-date'))
              break
            case 'auth/login-expired':
              this.commonError.set(this._translationService.lokalisoi('login.login-out-of-date'))
              break
            case 'auth/code-attempts-exceeded':
              this.commonError.set(this._translationService.lokalisoi('login.code-attempts-exceeded'))
              break
            case 'auth/user-disabled':
              this.commonError.set(this._translationService.lokalisoi('login.validation.email.user-disabled'))
              break
            case 'auth/user-inactive':
              this.commonError.set(this._translationService.lokalisoi('login.validation.email.user-disabled'))
              break
            default:
              this._errorHandler.handleError(error)
              this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe'))
          }
          this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
        } else {
          this._firebaseLemonaid
            .authSignInWithCustomToken(data.tokens.lemonaidToken)
            .then(() => {
              this.setLoginProcessEnded()
              this._loginService.clearCredentials()
              this._router.navigate(['/'])
            })
            .catch(err => {
              this._errorHandler.handleError(err)
              this.setLoginProcessEnded()
              const code = err['code']
              switch (code) {
                case 'auth/custom-token-mismatch':
                  this.commonError.set('Tämä token kuuluu toiseen projektiin.')
                  break
                case 'auth/invalid-custom-token':
                  this.commonError.set('Token on virheellinen.')
                  break
                case 'auth/user-disabled':
                  this.commonError.set(this._translationService.lokalisoi('login.validation.email.user-disabled'))
                  break
                default:
                  this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe'))
              }
              this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
            })
        }
      })
      .catch(err => {
        this._errorHandler.handleError(err)
        this.setLoginProcessEnded()
        this.commonError.set(this._translationService.lokalisoi('yleiset.tuntematon-virhe'))
        this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      })
  }

  vaihdaKieli() {
    const data: VaihdaKieliDialogData = {
      tallenna: false,
    }
    this._dialog.open(VaihdaKieliDialog, { data: data })
  }

  private _containsNonNumbersValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    if (control.value) {
      const trimmed = (control.value + '').trim()
      const containsNonNumbers = this._currencyService.poistaNumeroonKuulumattomatMerkit(trimmed, 0)?.length !== trimmed.length
      if (containsNonNumbers) {
        return { containsNonNums: true }
      }
    }
    return null
  }
}
